import React, { useEffect, useState } from 'react';
import Lottie from 'lottie-react';

interface IntroProps {
  onFinish: () => void;
}

export const Intro: React.FC<IntroProps> = ({ onFinish }) => {
  const [hasPlayed, setHasPlayed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('hasSeenIntro') === 'true';
    } catch (error) {
      console.warn('Erreur localStorage:', error);
      return false;
    }
  });

  const [animationData, setAnimationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Charger l'animation Lottie
  useEffect(() => {
    const loadAnimation = async () => {
      try {
        // Essayer de charger l'animation depuis le fichier
        const response = await fetch('/src/animations/intro-animation.json');
        if (!response.ok) {
          throw new Error('Animation file not found');
        }
        const data = await response.json();
        setAnimationData(data);
      } catch (err) {
        console.warn('Impossible de charger l\'animation Lottie:', err);
        setError(true);
        // Si l'animation ne peut pas être chargée, passer directement à la fin
        setTimeout(() => {
          try {
            localStorage.setItem('hasSeenIntro', 'true');
          } catch (error) {
            console.warn('Erreur localStorage:', error);
          }
          onFinish();
        }, 500);
      } finally {
        setIsLoading(false);
      }
    };

    if (!hasPlayed) {
      loadAnimation();
    }
  }, [hasPlayed, onFinish]);

  useEffect(() => {
    if (hasPlayed) {
      onFinish();
      return;
    }

    // Timeout de sécurité pour éviter le blocage
    const safetyTimeout = setTimeout(() => {
      console.warn('Timeout de sécurité atteint, fin forcée de l\'intro');
      try {
        localStorage.setItem('hasSeenIntro', 'true');
        setHasPlayed(true);
        onFinish();
      } catch (error) {
        console.warn('Erreur localStorage:', error);
        onFinish();
      }
    }, 5000); // 5 secondes maximum

    return () => clearTimeout(safetyTimeout);
  }, [hasPlayed, onFinish]);

  // Si déjà vu ou erreur, ne rien afficher
  if (hasPlayed || error) return null;

  // Si en cours de chargement
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  const handleAnimationComplete = () => {
    try {
      localStorage.setItem('hasSeenIntro', 'true');
      setHasPlayed(true);
      onFinish();
    } catch (error) {
      console.warn('Erreur localStorage:', error);
      setHasPlayed(true);
      onFinish();
    }
  };

  const handleSkip = () => {
    try {
      localStorage.setItem('hasSeenIntro', 'true');
      setHasPlayed(true);
      onFinish();
    } catch (error) {
      console.warn('Erreur localStorage:', error);
      setHasPlayed(true);
      onFinish();
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
      <div className="relative w-full h-full flex items-center justify-center">
        {animationData ? (
          <>
            <Lottie
              animationData={animationData}
              loop={false}
              autoplay={true}
              style={{ width: '70vw', height: '70vh', maxWidth: '800px', maxHeight: '600px' }}
              onComplete={handleAnimationComplete}
              onError={(error) => {
                console.error('Erreur Lottie:', error);
                handleAnimationComplete();
              }}
            />
            {/* Bouton pour passer l'intro */}
            <button
              onClick={handleSkip}
              className="absolute top-8 right-8 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
              aria-label="Passer l'introduction"
            >
              Passer
            </button>
          </>
        ) : (
          <div className="text-white text-center">
            <div className="animate-pulse">
              <div className="w-32 h-32 bg-orange-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-4xl font-bold">R</span>
              </div>
              <h1 className="text-3xl font-bold mb-2">Restomi Kiosk</h1>
              <p className="text-lg opacity-80">Bienvenue</p>
            </div>
            <button
              onClick={handleSkip}
              className="mt-8 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Continuer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};